import type { Selector } from '@rematch/select';
import Cookies from 'universal-cookie';

import { call, get as embeddedGet } from '@swf/core/api/embedded';
import { Currency, Site } from '@swf/core/enums';
import type { SiteState } from '@swf/core/models/site';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import CookieNames from '@swf/core/types/Cookies';

export interface CurrencyState {
  site: Currency;
  selected?: Currency;
  selfLimitCurrency?: Currency;
}

const SiteCurrency = {
  [Site.ASIA]: Currency.USDollar,
  [Site.BE]: Currency.USDollar,
  [Site.BG]: Currency.USDollar,
  [Site.COM]: Currency.USDollar,
  [Site.CZ]: Currency.USDollar,
  [Site.DESH]: Currency.USDollar,
  [Site.DK]: Currency.USDollar,
  [Site.EE]: Currency.USDollar,
  [Site.ES]: Currency.Euro,
  [Site.EU]: Currency.Euro,
  [Site.FR]: Currency.Euro,
  [Site.IN]: Currency.IndianRupee,
  [Site.IT]: Currency.Euro,
  [Site.NET]: Currency.USDollar,
  [Site.USNJ]: Currency.USDollar,
  [Site.PT]: Currency.Euro,
  [Site.RO]: Currency.RomanianLeu,
  [Site.RU]: Currency.RussianRuble,
  [Site.RUSO]: Currency.USDollar,
  [Site.RUSOPM]: Currency.USDollar,
  [Site.UK]: Currency.BritishPound
};

interface CurrencyModel extends ModelConfig<CurrencyState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const cookies = new Cookies();

const reducers = {
  'site/set': (state: CurrencyState, { id }: SiteState): CurrencyState => ({
    ...state,
    site: SiteCurrency[id]
  }),
  set: (state: CurrencyState, { selected }: { selected?: Currency }): CurrencyState => ({
    ...state,
    selected
  }),
  setSelfLimitCurrency: (
    state: CurrencyState,
    { selfLimitCurrency }: { selfLimitCurrency: Currency }
  ): CurrencyState => ({
    ...state,
    selfLimitCurrency
  })
};

const effects = {
  async setPlayMoney({ selected }: { selected: boolean }, { rootState, dispatch }: EffectParams) {
    if (select.site.isEmbedded(rootState)) {
      call(['app', 'setPlayMoney'], selected);
    } else {
      dispatch.currency.set({ selected: selected ? Currency.PlayMoney : undefined });
      selected
        ? cookies.set(CookieNames.CURRENCY, Currency.PlayMoney)
        : cookies.remove(CookieNames.CURRENCY);
    }
  }
};

const selectors = (slice: Slicer<CurrencyState>, createSelector: SelectorCreator) => ({
  get() {
    return createSelector(
      (rootState) => select.account.getCurrency(rootState),
      slice as Selector<RootState, CurrencyState>,
      (accountCurrency, state) => state.selected || accountCurrency || state.site
    );
  },
  getRealMoney() {
    return createSelector(
      (rootState) => select.account.getCurrency(rootState),
      slice as Selector<RootState, CurrencyState>,
      (accountCurrency, state) => accountCurrency || state.site
    );
  },
  isPlayMoney() {
    return slice((state) => state.selected === Currency.PlayMoney);
  },
  getSelfLimitCurrency(this: { getRealMoney: Selector<RootState, Currency> }) {
    return createSelector(
      slice as Selector<RootState, CurrencyState>,
      this.getRealMoney,
      (state, currency) => state.selfLimitCurrency || currency
    );
  }
});

const currency: CurrencyModel = {
  state: {
    site: Currency.USDollar,
    selected: embeddedGet(['app', 'playMoney']) || cookies.get(CookieNames.CURRENCY)
  },
  reducers,
  effects,
  selectors
};

export default currency;
